import { useCallback, useEffect, useState } from 'react';

// Daily-summary panel state + handlers. Manages picked day, hover/click
// highlighting, the persisted "default open" preference, and the
// scroll-to-event behaviour when the user clicks an event pill.
export function useDailySummary({ allEventsForSummary, visibleISOs, defaultSummaryISO }) {
  const [summaryDateISO, setSummaryDateISO] = useState(null);
  const [hoveredEventNumber, setHoveredEventNumber] = useState(null);
  const [clickedEventNumber, setClickedEventNumber] = useState(null);
  const [showDailySummary, setShowDailySummary] = useState(false);

  // Persist whether the summary should auto-open by default — same key the
  // main app uses, so the preference is shared across / and /v2.
  const [dailySummaryDefaultOpen, setDailySummaryDefaultOpen] = useState(() => {
    const saved = localStorage.getItem('dailySummaryDefaultOpen');
    return saved ? JSON.parse(saved) : true;
  });

  const effectiveSummaryISO = (summaryDateISO && visibleISOs.includes(summaryDateISO))
    ? summaryDateISO
    : defaultSummaryISO;

  // Auto-open when events are available and the user hasn't explicitly closed.
  useEffect(() => {
    if (dailySummaryDefaultOpen && allEventsForSummary.length > 0 && !showDailySummary) {
      setShowDailySummary(true);
    }
  }, [allEventsForSummary.length, dailySummaryDefaultOpen, showDailySummary]);

  // Closing flips the persisted preference so it stays closed next time.
  const handleClose = useCallback(() => {
    setShowDailySummary(false);
    const next = !dailySummaryDefaultOpen;
    setDailySummaryDefaultOpen(next);
    localStorage.setItem('dailySummaryDefaultOpen', JSON.stringify(next));
  }, [dailySummaryDefaultOpen]);

  const handleOpen = useCallback(() => {
    setShowDailySummary(true);
    if (!dailySummaryDefaultOpen) {
      setDailySummaryDefaultOpen(true);
      localStorage.setItem('dailySummaryDefaultOpen', JSON.stringify(true));
    }
  }, [dailySummaryDefaultOpen]);

  // Opens the summary, scrolls to that event, briefly highlights it. In
  // multi-day views the day the pill belongs to also becomes the picked
  // summary day so numbers stay consistent.
  const openAndScrollToEvent = useCallback((dateISO, eventNumber) => {
    if (!eventNumber) return;
    if (dateISO) setSummaryDateISO(dateISO);
    setShowDailySummary(true);
    setClickedEventNumber(eventNumber);
    if (!dailySummaryDefaultOpen) {
      setDailySummaryDefaultOpen(true);
      localStorage.setItem('dailySummaryDefaultOpen', JSON.stringify(true));
    }
    setTimeout(() => {
      const el = document.getElementById(`event-${eventNumber}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    setTimeout(() => setClickedEventNumber(null), 2000);
  }, [dailySummaryDefaultOpen]);

  return {
    showDailySummary,
    summaryDateISO,
    setSummaryDateISO,
    effectiveSummaryISO,
    hoveredEventNumber,
    setHoveredEventNumber,
    clickedEventNumber,
    setClickedEventNumber,
    handleClose,
    handleOpen,
    openAndScrollToEvent,
    dailySummaryDefaultOpen,
  };
}
